// orders/orders.js

import React, { useState, useEffect, useMemo } from "react";
import Pagenation from "./pagenation";
import SearchTable from "./search";

const LOGS_PER_PAGE = 10;

export default function LogsData() {
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKENDORDERURL}/logs`
        );
        const data = await res.json();
        if (data && data.data) {
          setLogs(data.data);
        }
        if (!res.ok || !data.success) {
          if (res.status === 401) {
          alert("Session expired. Please login again.", "error");
          // Optional: logout user
          localStorage.removeItem("token");
          return;
        }
      }
      } catch (error) {
        console.error("Error fetching logs:", error);
      }
    };
    fetchLogs();
  }, []);

  // Search logic: search in top-level fields and nested previousState/changedsApplied fields
  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;
    const lower = searchTerm.toLowerCase();
    return logs.filter((log) => {
      // Search in top-level fields
      if (
        log._id?.toLowerCase().includes(lower) ||
        log.actionType?.toLowerCase().includes(lower) ||
        log.collectionName?.toLowerCase().includes(lower) ||
        log.userId?.toLowerCase().includes(lower) ||
        log.documentId?.toLowerCase().includes(lower) ||
        (log.timestamp &&
          new Date(log.timestamp)
            .toLocaleString()
            .toLowerCase()
            .includes(lower))
      )
        return true;
      // Search in previousState and changesApplied
      const prev = log.previousState || {};
      const changes = log.changesApplied || {};
      for (const key in prev) {
        if (String(prev[key]).toLowerCase().includes(lower)) return true;
      }
      for (const key in changes) {
        if (String(changes[key]).toLowerCase().includes(lower)) return true;
      }
      return false;
    });
  }, [logs, searchTerm]);

  const totalPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * LOGS_PER_PAGE,
    currentPage * LOGS_PER_PAGE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  return (
    <section id="logs-section" className="content-body">
      <h2>Logs</h2>
      <SearchTable
        aggregators={[]}
        onSearch={handleSearch}
        onAggregatorChange={() => {}}
      />
      <div className="table-responsive-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action Type</th>
              <th>Collection</th>
              <th>User</th>
              <th>Document ID</th>
              <th>Previous State</th>
              <th>Changes Applied</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLogs.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No logs found
                </td>
              </tr>
            ) : (
              paginatedLogs.map((log) => (
                <tr key={log._id}>
                  <td>
                    {log.timestamp
                      ? new Date(log.timestamp).toLocaleString()
                      : ""}
                  </td>
                  <td>{log.actionType}</td>
                  <td>{log.collectionName}</td>
                  <td>{log.userId}</td>
                  <td>{log.documentId}</td>
                  <td>
                    <details>
                      <summary>View</summary>
                      <pre style={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(log.previousState, null, 2)}
                      </pre>
                    </details>
                  </td>
                  <td>
                    <details>
                      <summary>View</summary>
                      <pre style={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(log.changesApplied, null, 2)}
                      </pre>
                    </details>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagenation
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={filteredLogs.length}
        itemsPerPage={LOGS_PER_PAGE}
      />
    </section>
  );
}
